import React from 'react'
import { Box, Container, Stack } from '@mui/material'

import type { HolidayNavTab, HolidayCollection } from '@/src/types/holidays'
import HeaderContainer from '@/src/components/common/header/HeaderRouter'
import HolidaysFooter from '@/src/components/desktop/homepageFooter'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'

import HolidayBreadcrumbsWrapper from '@/src/components/common/holidays/holidayBreadcrumbs'
import CollectionHeroWrapper from '@/src/components/common/holidays/collectionHero'
import HolidayNavTabsWrapper from '@/src/components/common/holidays/holidayNavTabs'
import HolidaySectionWrapper from '@/src/components/common/holidays/holidaySection'
import { HOLIDAYS_MAX_WIDTH } from '@/src/utils/holidays'

interface HolidayCollectionDesktopPageProps {
    collection: HolidayCollection
    navTabs: HolidayNavTab[]
    domainCountry: string
}

const HolidayCollectionDesktopPage: React.FC<HolidayCollectionDesktopPageProps> = ({
    collection,
    navTabs,
    domainCountry
}) => {
    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: 'background.default',
                display: 'flex',
                flexDirection: 'column',
                // See the note in the package page: `hidden` would break the sticky tab band.
                overflowX: 'clip'
            }}
        >
            <ErrorBoundaryWrapper identifier="holidays-collection-header-desktop">
                <HeaderContainer domainCountry={domainCountry} activeVertical="holidays" />
            </ErrorBoundaryWrapper>

            {/* The breadcrumb sits with the heading it belongs to, below the banner. */}
            <ErrorBoundaryWrapper identifier="holidays-collection-hero-desktop">
                <CollectionHeroWrapper
                    breadcrumbs={
                        <ErrorBoundaryWrapper identifier="holidays-collection-breadcrumbs-desktop">
                            <HolidayBreadcrumbsWrapper currentLabel={collection.hero.breadcrumbLabel} />
                        </ErrorBoundaryWrapper>
                    }
                    collection={collection}
                    isMobile={false}
                />
            </ErrorBoundaryWrapper>

            {/* The nav tabs no longer stick on their own — the page owns the pinned band. The band
                spans the viewport so its background masks content scrolling under it, but the pill
                inside sits in the same Container as everything else on the page. */}
            <Box sx={{ position: 'sticky', top: 0, zIndex: 11, bgcolor: 'background.default', py: 1.25 }}>
                <Container maxWidth={false} sx={{ maxWidth: HOLIDAYS_MAX_WIDTH }}>
                    <ErrorBoundaryWrapper identifier="holidays-collection-nav-desktop">
                        <HolidayNavTabsWrapper tabs={navTabs} isMobile={false} />
                    </ErrorBoundaryWrapper>
                </Container>
            </Box>

            <Container component="main" maxWidth={false} sx={{ maxWidth: HOLIDAYS_MAX_WIDTH, py: 4 }}>
                <Stack spacing={5}>
                    {collection.sections.map(section => (
                        <ErrorBoundaryWrapper
                            key={section.id}
                            identifier={`holidays-collection-section-${section.id}-desktop`}
                        >
                            <HolidaySectionWrapper
                                section={section}
                                collectionSlug={collection.slug}
                                variant="plain"
                                isMobile={false}
                            />
                        </ErrorBoundaryWrapper>
                    ))}
                </Stack>
            </Container>

            <Box sx={{ mt: 'auto' }}>
                <HolidaysFooter showHomeHighlights={false} />
            </Box>
        </Box>
    )
}

export { HolidayCollectionDesktopPage }
export default HolidayCollectionDesktopPage
